// Version: 2.10
// v2.10: Buttons use the shared button system (CV-49): 8px corners, standard variants and sizes, one selected look; no inline radius, padding or font size.
// v2.9: The Reports button opens the client in Client Management on its
//       Reports tab (/manage?client=<slug>&tab=reports, ClientManagement.js
//       v3.84); it went to the client's dashboard.
// v2.8: Progress bars follow the pipeline's own log lines, so every tool shows.
//       - A tool is running once the pipeline logs the stage that contains it
//         ("[*] Stage 3: dns + osint + ..."), not only when the tool prints its
//         own lines. OSINT and the report generator buffer their output, so
//         their bars sat at ○ for the whole run.
//       - A tool the pipeline stops for running out of time shows as amber
//         "timed out" instead of staying blank; it counts towards the overall
//         percentage.
//       - New External Intelligence bar (the intel tool, run since pipeline
//         v2.5 but never shown); it fills the empty slot in the grid.
//       - OSINT bar renamed Exposed Pages and Threat Feeds, and no longer marked
//         complete by its own "theHarvester complete" line.
//       - Header shows the stage in progress, or "Generating report" late in a
//         run, rather than "Starting...".
//       - "Abuse & Reputation" renamed Abuse and Reputation.
// v2.7: Empty state wording and link use the agreed name; the link opens the
//       Reports and Assessments page with the assessment option selected.
// v2.6: Added the m365recon stage ("Productivity Suite Check") so it shows a
//        progress bar alongside the other tools.
// v2.5: Live VPN badge on each scan card. The assessment log (already
//        streamed over SSE) prints the VPN state, so the card derives it
//        from its own lines — no backend change. States: connecting (amber,
//        pulsing), connected (green), reconnecting (red, pulsing), off (grey).
//        Hidden once the scan is done.
import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { authFetch, getSseToken } from './auth';
import './ActiveScans.css';

const NOISE_PATTERNS = [
  /^\[\*\] Still running\.\.\./,
  /^\[\*\] Waiting for assessment/,
  /^\[\*\] Pulling images/,
  /Skipped - Image is already/,
  /pulling fs layer/i,
  /Downloading \[/,
  /Extracting \[/,
  /Pull complete/,
  /Already exists/,
  /Verifying Checksum/,
  /Download complete/,
  /WARNING: Running pip/,
  /notice\] A new release of pip/,
  /notice\] To update, run/,
  /^\s*$/,
];

const ADMIN_STAGES = [
  { key: 'harvester',    label: 'Reconnaissance'      },
  { key: 'testssl',      label: 'SSL and TLS'         },
  { key: 'm365recon',    label: 'Productivity Suite Check' },
  { key: 'dns',          label: 'DNS Analysis'        },
  { key: 'abuseipdb',    label: 'Abuse and Reputation' },
  { key: 'osint',        label: 'Exposed Pages and Threat Feeds' },
  { key: 'shodan',       label: 'Internet Exposure'   },
  { key: 'headers',      label: 'Headers Analysis'    },
  { key: 'cookies',      label: 'Cookie Compliance'   },
  { key: 'blacklist',    label: 'Blacklist Check'     },
  { key: 'tldsweep',     label: 'Brand Sweep'         },
  { key: 'wpscan',       label: 'WordPress Scan'      },
  { key: 'knockpy',      label: 'Subdomain Enum'      },
  { key: 'nikto',        label: 'Web Server Scan'     },
  { key: 'rustscan',     label: 'Port Scan'           },
  { key: 'baddns',       label: 'Takeover Check'      },
  { key: 'nuclei',       label: 'Vulnerability Scan'  },
  { key: 'buckets',      label: 'Cloud Storage'       },
  { key: 'credexposure', label: 'Credential Exposure' },
  { key: 'dbexposure',   label: 'Database Exposure'   },
  { key: 'hudsonrock',   label: 'Infostealer Exposure' },
  { key: 'intel',        label: 'External Intelligence' },
  { key: 'reporter',     label: 'Generating Report'   },
];

const HARVESTER_DEPENDENT = new Set([
  'm365recon','dns','abuseipdb','osint','shodan','headers','cookies','blacklist',
  'tldsweep','wpscan','knockpy','nikto','rustscan','baddns',
  'nuclei','buckets','credexposure','dbexposure','hudsonrock'
]);

const TOOL_START_PATTERNS = {
  harvester:   /\[theHarvester\].*[Ss]canning domain|\[theHarvester\].*[Ss]tarting/i,
  testssl:     /\[testssl\].*[Cc]hecking domain|\[testssl\].*[Ss]tarting/i,
  m365recon:   /\[M365Recon\]/i,
  dns:         /\[DNS\]/i,
  abuseipdb:   /\[AbuseIPDB\]/i,
  osint:       /\[OSINT\]/i,
  shodan:      /\[Shodan\]/i,
  headers:     /\[Headers\]/i,
  cookies:     /\[Cookies\]/i,
  blacklist:   /\[Blacklist\]/i,
  tldsweep:    /\[TLDSweep\]/i,
  wpscan:      /\[WPScan\]/i,
  knockpy:     /\[Knockpy\]/i,
  nikto:       /\[Nikto\]/i,
  rustscan:    /\[rustscan\]/i,
  baddns:      /\[BadDNS\]|\[baddns\]/i,
  nuclei:      /\[Nuclei\]/i,
  buckets:     /\[Buckets\]/i,
  credexposure:/\[CredExposure\]/i,
  dbexposure:  /\[DBExposure\]/i,
  hudsonrock:  /\[HudsonRock\]/i,
  intel:       /\[Intel\]/i,
  reporter:    /\[Assessment\].*[Gg]enerating|\[Assessment\].*[Rr]eport saved/i,
};

const TOOL_PROGRESS_PATTERNS = {
  harvester:   /\[theHarvester\].*[Ss]canning domain (\d+)\/(\d+)/i,
  testssl:     /\[testssl\].*[Cc]hecking domain (\d+)\/(\d+)/i,
  tldsweep:    /\[TLDSweep\].*[Ss]weeping brand (\d+)\/(\d+)/i,
  cookies:     /\[Cookies\].*[Ss]canning target (\d+)\/(\d+)/i,
  wpscan:      /\[WPScan\].*[Ss]canning target (\d+)\/(\d+)/i,
  knockpy:     /\[Knockpy\].*(\d+)\/(\d+) words checked/i,
  rustscan:    /\[rustscan\].*[Ss]canning IP (\d+)\/(\d+)/i,
};

const TOOL_COMPLETE_PATTERNS = {
  harvester:   /\[Assessment\].*[Hh]arvester.*complete|\[theHarvester\].*[Aa]ll domains complete/i,
  testssl:     /\[Assessment\].*testssl.*complete|\[testssl\].*[Aa]ll SSL checks complete/i,
  m365recon:   /\[Assessment\].*m365recon.*complete|\[M365Recon\].*Done/i,
  dns:         /\[Assessment\].*DNS.*complete|\[DNS\].*[Cc]omplete/i,
  abuseipdb:   /\[Assessment\].*AbuseIPDB.*complete|\[AbuseIPDB\].*[Cc]omplete/i,
  osint:       /\[Assessment\] osint complete|\[OSINT\] Complete\./i,
  shodan:      /\[Assessment\].*Shodan.*complete|\[Shodan\].*[Cc]omplete/i,
  headers:     /\[Assessment\].*[Hh]eaders.*complete|\[Headers\].*[Cc]omplete/i,
  cookies:     /\[Assessment\].*[Cc]ookies.*complete|\[Cookies\].*[Cc]omplete/i,
  blacklist:   /\[Assessment\].*[Bb]lacklist.*complete|\[Blacklist\].*[Cc]omplete/i,
  tldsweep:    /\[Assessment\].*TLDSweep.*complete|\[TLDSweep\].*[Cc]omplete/i,
  wpscan:      /\[Assessment\].*WPScan.*complete|\[WPScan\].*[Cc]omplete/i,
  knockpy:     /\[Assessment\].*Knockpy.*complete|\[Knockpy\].*[Tt]otal/i,
  nikto:       /\[Assessment\].*Nikto.*complete|\[Nikto\].*[Cc]omplete/i,
  rustscan:    /\[Assessment\].*RustScan.*complete|\[rustscan\].*[Cc]omplete/i,
  baddns:      /\[Assessment\].*BadDNS.*complete|\[BadDNS\].*[Cc]omplete/i,
  nuclei:      /\[Assessment\].*Nuclei.*complete|\[Nuclei\].*[Cc]omplete/i,
  buckets:     /\[Assessment\].*[Bb]ucket.*complete|\[Buckets\].*[Cc]omplete/i,
  credexposure:/\[Assessment\].*[Cc]redential.*complete|\[CredExposure\].*[Cc]omplete/i,
  dbexposure:  /\[Assessment\].*[Dd]atabase.*complete|\[DBExposure\].*[Cc]omplete/i,
  hudsonrock:  /\[Assessment\].*HudsonRock.*complete|\[HudsonRock\].*[Ee]xiting cleanly/i,
  intel:       /\[Assessment\] intel complete|\[Intel\] Complete\./i,
  reporter:    /\[Assessment\].*Grade:/i,
};

function parseToolProgress(lines) {
  const joined = lines.join('\n');
  const harvesterDone = TOOL_COMPLETE_PATTERNS['harvester'].test(joined);
  const rustscanDone  = TOOL_COMPLETE_PATTERNS['rustscan'].test(joined);
  const state = {};

  // v2.8: the pipeline's own lines — "[*] Stage 3: dns + osint + ...",
  // "[Assessment] osint complete in 412s", "[Assessment] osint timed out ..."
  const stageStarted = new Set();
  const pipelineDone = new Set();
  const timedOut     = new Set();
  lines.forEach(line => {
    let m = line.match(/\[\*\] Stage \d+:\s*(.+)$/);
    if (m) m[1].split('+').forEach(t => stageStarted.add(t.trim()));
    m = line.match(/\[Assessment\] (\w+) complete in \d+s/);
    if (m) pipelineDone.add(m[1]);
    m = line.match(/\[Assessment\] (\w+) timed out/);
    if (m) timedOut.add(m[1]);
  });

  ADMIN_STAGES.forEach(({ key }) => {
    const started   = stageStarted.has(key) || (TOOL_START_PATTERNS[key] ? TOOL_START_PATTERNS[key].test(joined) : false);
    const completed = pipelineDone.has(key) || (TOOL_COMPLETE_PATTERNS[key] ? TOOL_COMPLETE_PATTERNS[key].test(joined) : false);
    let pct = 0, status = 'queued';

    if (timedOut.has(key) && !pipelineDone.has(key)) {
      pct = 100; status = 'timeout';
    } else if (completed) {
      pct = 100; status = 'done';
    } else if (started) {
      status = 'running';
      const prog = TOOL_PROGRESS_PATTERNS[key];
      if (prog) {
        let lastX = null, lastY = null;
        lines.forEach(line => {
          const m = line.match(prog);
          if (m) { lastX = parseInt(m[1]); lastY = parseInt(m[2]); }
        });
        pct = (lastX !== null && lastY > 0) ? Math.round((lastX / lastY) * 95) : 5;
      } else { pct = 5; }
    }

    if (HARVESTER_DEPENDENT.has(key) && !harvesterDone && status === 'queued') status = 'waiting';
    if (['nuclei','dbexposure'].includes(key) && !rustscanDone && status !== 'done') {
      if (status !== 'running') status = 'waiting';
    }
    state[key] = { status, pct };
  });
  return state;
}

function ToolBar({ label, status, pct, maxCreep }) {
  const displayRef = useRef(0);
  const [displayPct, setDisplayPct] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    const finished = status === 'done' || status === 'timeout';
    const target = finished ? 100 : pct;
    if (target <= displayRef.current && !finished) return;
    const animate = () => {
      displayRef.current = Math.min(target, displayRef.current + Math.min(0.5, Math.max(0.05, (target - displayRef.current) * 0.05)));
      setDisplayPct(Math.round(displayRef.current * 10) / 10);
      if (displayRef.current < target) rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [pct, status]);

  useEffect(() => {
    if (status !== 'running') return;
    const interval = setInterval(() => {
      if (displayRef.current < (maxCreep || 90)) {
        displayRef.current += 0.04;
        setDisplayPct(Math.round(displayRef.current * 10) / 10);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [status, maxCreep]);

  const barColor   = status === 'done' ? '#30d158' : status === 'timeout' ? '#e6b800' : status === 'running' ? '#00d4ff' : '#2a2a2e';
  const textColor  = status === 'done' ? '#30d158' : status === 'timeout' ? '#e6b800' : status === 'running' ? '#00d4ff' : '#555';
  const icon       = status === 'done' ? '✓' : status === 'timeout' ? '⚠' : status === 'running' ? '●' : '○';
  const full       = status === 'done' || status === 'timeout';

  return (
    <div style={{marginBottom:7}}>
      <div style={{display:'flex',justifyContent:'space-between',marginBottom:2}}>
        <span style={{fontSize:11,color:textColor,fontWeight:status==='running'?600:400}}>
          {icon} {label}
        </span>
        <span style={{fontSize:10,color:textColor,fontFamily:'monospace'}}>
          {status==='waiting'?'waiting':status==='queued'?'—':status==='timeout'?'timed out':`${Math.round(status==='done'?100:displayPct)}%`}
        </span>
      </div>
      <div style={{height:3,background:'#1a1a1e',borderRadius:2,overflow:'hidden'}}>
        <div style={{
          height:'100%',
          width:`${full?100:displayPct}%`,
          background:barColor,
          borderRadius:2,
          boxShadow:status==='running'?`0 0 4px ${barColor}`:'none',
          transition:full?'width 0.3s ease':'none'
        }} />
      </div>
    </div>
  );
}

function formatElapsed(started) {
  const secs = Math.floor((Date.now() - new Date(started + 'Z').getTime()) / 1000);
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) return `${Math.floor(secs/60)}m ${secs%60}s`;
  return `${Math.floor(secs/3600)}h ${Math.floor((secs%3600)/60)}m`;
}

function vpnStateFromLines(lines) {
  // Walk the log in order; the last VPN marker wins. Returns one of
  // 'off' | 'connecting' | 'connected' | 'reconnecting' | null (unknown yet).
  let state = null;
  for (const l of lines) {
    if (/VPN:\s*off/i.test(l))                         state = 'off';
    else if (/VPN:\s*on/i.test(l))                     state = 'connecting';
    else if (/Connecting VPN/i.test(l))                state = 'connecting';
    else if (/VPN connected/i.test(l))                 state = 'connected';
    else if (/VPN unhealthy|VPN dropped/i.test(l))     state = 'reconnecting';
    else if (/Restarting assessment/i.test(l) && state && state !== 'off') state = 'connecting';
  }
  return state;
}

const VPN_META = {
  connecting:   { label: 'VPN connecting',   color: '#ff9f0a', pulse: true  },
  connected:    { label: 'VPN connected',    color: '#30d158', pulse: false },
  reconnecting: { label: 'VPN reconnecting', color: '#ff2d55', pulse: true  },
  off:          { label: 'No VPN',           color: '#86868b', pulse: false },
};

function VpnBadge({ state }) {
  if (!state) return null;
  const m = VPN_META[state];
  if (!m) return null;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '2px 8px', borderRadius: 20, marginTop: 4,
      background: `${m.color}1f`, color: m.color,
      fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
    }}>
      <span style={{
        width: 6, height: 6, borderRadius: '50%', background: m.color,
        animation: m.pulse ? 'vpnpulse 1.2s ease-in-out infinite' : 'none',
      }} />
      {m.label}
    </span>
  );
}

function ScanCard({ scan, onCancelled }) {
  const [lines, setLines]               = useState([]);
  const [consoleLines, setConsoleLines] = useState([]);
  const [done, setDone]                 = useState(false);
  const [elapsed, setElapsed]           = useState('');
  const [expanded, setExpanded]         = useState(false);
  const [viewMode, setViewMode]         = useState('progress');
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [cancelling, setCancelling]       = useState(false);
  const esRef  = useRef(null);
  const logRef = useRef(null);

  useEffect(() => {
    let es;
    getSseToken().then(sseToken => {
      es = new EventSource(`/api/assessments/${scan.slug}/logs?token=${sseToken}`);
      esRef.current = es;
      es.onmessage = (e) => {
        if (e.data === '[DONE]') { setDone(true); es.close(); }
        else {
          const line = e.data;
          setLines(prev => [...prev, line]);
          if (!NOISE_PATTERNS.some(p => p.test(line))) {
            setConsoleLines(prev => [...prev, line].slice(-200));
          }
        }
      };
      es.onerror = () => es.close();
    });
    const timer = setInterval(() => setElapsed(formatElapsed(scan.started)), 1000);
    setElapsed(formatElapsed(scan.started));
    return () => { if (esRef.current) esRef.current.close(); clearInterval(timer); };
  }, [scan.slug]);

  useEffect(() => {
    if (viewMode === 'console' && expanded && logRef.current)
      logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [consoleLines, expanded, viewMode]);

  const handleCancel = async (e) => {
    e.stopPropagation();
    if (!cancelConfirm) { setCancelConfirm(true); return; }
    setCancelling(true);
    setCancelConfirm(false);
    try {
      await authFetch(`/api/assessments/${scan.slug}/cancel`, { method: 'POST' });
      if (esRef.current) esRef.current.close();
      if (onCancelled) onCancelled(scan.slug);
    } catch { /* ignore */ }
    setCancelling(false);
  };

  const toolState      = parseToolProgress(lines);
  const completedCount = ADMIN_STAGES.filter(s => ['done','timeout'].includes(toolState[s.key]?.status)).length;
  const overallPct     = done ? 100 : Math.round((completedCount / ADMIN_STAGES.length) * 100);
  const lastRunning    = [...ADMIN_STAGES].reverse().find(s => toolState[s.key]?.status === 'running');
  const anyFinished    = completedCount > 0;
  const currentLabel   = done ? 'Complete'
                       : lastRunning ? (lastRunning.key === 'reporter' ? 'Generating report' : lastRunning.label)
                       : (anyFinished ? (completedCount >= ADMIN_STAGES.length - 1 ? 'Generating report' : 'Between stages') : 'Starting...');
  const vpnState       = done ? null : vpnStateFromLines(lines);

  const getLineColor = (line) => {
    if (/complete\.|Complete\.|✔|Done\./.test(line)) return '#30d158';
    if (/\[ERROR\]|error|Error|FAIL|failed/i.test(line)) return '#ff2d55';
    if (/WARNING|warning|⚠/i.test(line)) return '#e6b800';
    if (/^\[\*\]/.test(line)) return '#00d4ff';
    if (/^\[.*?\]/.test(line)) return '#e5e5ea';
    return '#86868b';
  };

  return (
    <div className={`scan-card${done ? ' scan-card-done' : ''}`}>
      <style>{`@keyframes vpnpulse{0%,100%{opacity:1}50%{opacity:0.3}}`}</style>
      <div className="scan-card-header" onClick={() => setExpanded(e => !e)}>
        <div className="scan-card-left">
          {done ? <span className="done-dot" /> : <span className="pulse-dot" />}
          <div>
            <div className="scan-card-name">{scan.client_name.replace(/_/g,' ')}</div>
            <div className="scan-card-meta">{currentLabel} · {elapsed} · by {scan.initiated_by}</div>
            <VpnBadge state={vpnState} />
          </div>
        </div>
        <div className="scan-card-right">
          <div className="scan-card-pct">{overallPct}%</div>
          <Link className="btn btn-secondary btn-sm" to={`/manage?client=${encodeURIComponent(scan.slug)}&tab=reports`}
            onClick={e => e.stopPropagation()}>Reports</Link>
          {!done && (
            cancelConfirm ? (
              <>
                <button className="btn btn-danger btn-sm"
                 
                 
                  disabled={cancelling}
                  onClick={handleCancel}>
                  {cancelling ? <span className="spinner" /> : 'Confirm'}
                </button>
                <button className="btn btn-secondary btn-sm"
                 
                 
                  onClick={e => { e.stopPropagation(); setCancelConfirm(false); }}>
                  Keep
                </button>
              </>
            ) : (
              <button className="btn btn-secondary btn-sm"
               
               
                onClick={handleCancel}>
                Cancel
              </button>
            )
          )}
          <span className="scan-expand">{expanded ? '▲' : '▼'}</span>
        </div>
      </div>

      {/* Per-tool racing bars — always visible */}
      <div style={{padding:'10px 16px 6px',display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0 24px',borderTop:'1px solid var(--border)'}}>
        {ADMIN_STAGES.filter(s => s.key !== 'reporter').map(stage => (
          <ToolBar
            key={stage.key}
            label={stage.label}
            status={done ? (toolState[stage.key]?.status === 'timeout' ? 'timeout' : 'done') : (toolState[stage.key]?.status || 'queued')}
            pct={done ? 100 : (toolState[stage.key]?.pct || 0)}
            maxCreep={90}
          />
        ))}
        <div style={{gridColumn:'1 / -1'}}>
          <ToolBar
            label="Generating Report"
            status={done ? 'done' : (toolState['reporter']?.status || 'queued')}
            pct={done ? 100 : (toolState['reporter']?.pct || 0)}
            maxCreep={30}
          />
        </div>
      </div>

      {/* Expandable console */}
      {expanded && (
        <>
          <div style={{display:'flex',gap:8,padding:'6px 16px',borderTop:'1px solid var(--border)'}}>
            <button className="btn btn-secondary btn-sm" aria-pressed={viewMode==='console'}
              onClick={e => { e.stopPropagation(); setViewMode('console'); }}
             >
              Console
            </button>
          </div>
          <div className="scan-log" ref={logRef} onClick={e => e.stopPropagation()}>
            {consoleLines.length === 0 && (
              <div className="scan-log-line" style={{color:'#86868b',fontStyle:'italic'}}>Waiting for output...</div>
            )}
            {consoleLines.map((line, i) => (
              <div key={i} className="scan-log-line" style={{color: getLineColor(line)}}>{line || ' '}</div>
            ))}
            {!done && <div className="scan-log-line" style={{color:'#00d4ff'}}>▌</div>}
          </div>
        </>
      )}
    </div>
  );
}

export default function ActiveScans() {
  const [scans, setScans]             = useState([]);
  const [failedScans, setFailedScans] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [clearing, setClearing]       = useState({});
  const [flashMsg, setFlashMsg]       = useState(null);

  const flash = (msg, ok = true) => {
    setFlashMsg({ msg, ok });
    setTimeout(() => setFlashMsg(null), 5000);
  };

  const fetchFailed = () => {
    authFetch('/api/admin/failed-scans')
      .then(r => r.json())
      .then(d => setFailedScans(Array.isArray(d) ? d : []))
      .catch(() => {});
  };

  const fetchActive = () => {
    authFetch('/api/assessments/active')
      .then(r => r.json())
      .then(d => { setScans(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  const clearFailed = async (slug) => {
    setClearing(c => ({...c, [slug]: true}));
    try {
      const res = await authFetch(`/api/admin/clear-failed-scan/${slug}`, { method: 'POST' });
      const d = await res.json();
      flash(d.message || d.error, res.ok);
      fetchFailed();
    } finally {
      setClearing(c => ({...c, [slug]: false}));
    }
  };

  const handleCancelled = (slug) => {
    // Remove from active list immediately without waiting for next poll
    setScans(s => s.filter(sc => sc.slug !== slug));
    flash(`Scan cancelled`, true);
    setTimeout(() => fetchFailed(), 2000);
  };

  useEffect(() => {
    fetchActive();
    fetchFailed();
    const interval = setInterval(() => { fetchActive(); fetchFailed(); }, 10000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Active Scans</div>
        <div className="page-subtitle">Live assessment monitoring</div>
      </div>

      {flashMsg && (
        <div className={flashMsg.ok ? 'success-msg' : 'error-msg'} style={{marginBottom:12}}>{flashMsg.msg}</div>
      )}

      {failedScans.length > 0 && (
        <div className="card" style={{marginBottom:16,padding:'16px 20px'}}>
          <div style={{fontSize:13,fontWeight:700,color:'var(--red)',marginBottom:10}}>⚠ Failed Scans</div>
          <div style={{display:'flex',flexDirection:'column',gap:0}}>
            {failedScans.map(scan => (
              <div key={scan.slug} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 0',borderBottom:'1px solid var(--border)'}}>
                <div>
                  <div style={{fontSize:13,fontWeight:600,color:'var(--text)'}}>{scan.client_name.replace(/_/g,' ')}</div>
                  <div style={{fontSize:11,color:'var(--red)',fontFamily:'var(--font-mono)',marginTop:2}}>
                    {scan.cancelled ? 'Cancelled' : `Failed (exit ${scan.exit_code})`}
                    {scan.started ? ` · ${scan.started.slice(0,16).replace('T',' ')}` : ''}
                  </div>
                </div>
                <button className="btn btn-danger btn-sm" style={{ flexShrink: 0 }}
                  disabled={clearing[scan.slug]}
                  onClick={() => clearFailed(scan.slug)}>
                  {clearing[scan.slug] ? <span className="spinner" /> : 'Clear'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div style={{padding:48,textAlign:'center'}}><span className="spinner" /></div>
      ) : scans.length === 0 ? (
        <div className="card" style={{padding:48,textAlign:'center',color:'var(--text-muted)'}}>
          <div style={{fontSize:32,marginBottom:12}}>◎</div>
          <div style={{fontWeight:600,marginBottom:6}}>No active scans</div>
          <div style={{fontSize:13,marginBottom:20}}>Start an External Cyber Security Assessment to see it here</div>
          <Link className="btn btn-primary" to="/new?type=assessment">Run an Assessment</Link>
        </div>
      ) : (
        <div style={{display:'flex',flexDirection:'column',gap:12}}>
          {scans.map(scan => <ScanCard key={scan.slug} scan={scan} onCancelled={handleCancelled} />)}
        </div>
      )}
    </div>
  );
}